"use client";

import Link from "@/components/ui/CcLink";
import type { CSSProperties } from "react";

import { useSiteLocale } from "@/components/layout/CcLocaleContext";
import { getCommonCopy } from "@/lib/common-copy";
import type { HeroTickerItem } from "@/lib/services";

const HERO_TICKER_ENTER_BASE_DELAY_S = 0.48;
const HERO_TICKER_ENTER_STAGGER_S = 0.05;
const HERO_TICKER_ENTER_DURATION_S = 0.55;

type CcHeroServicesTickerProps = {
  items: HeroTickerItem[];
};

export function CcHeroServicesTicker({ items }: CcHeroServicesTickerProps) {
  const common = getCommonCopy(useSiteLocale());

  if (items.length === 0) {
    return null;
  }

  const loopItems = [...items, ...items];
  const scrollDelayS =
    HERO_TICKER_ENTER_BASE_DELAY_S +
    (items.length - 1) * HERO_TICKER_ENTER_STAGGER_S +
    HERO_TICKER_ENTER_DURATION_S;
  const trackStyle = {
    "--cc-hero-services-ticker-scroll-delay": `${scrollDelayS}s`,
  } as CSSProperties;

  return (
    <nav className="cc-hero__services-ticker" aria-label={common.servicesNavAria}>
      <div className="cc-hero__services-ticker-viewport">
        <ul className="cc-hero__services-ticker-track" style={trackStyle}>
          {loopItems.map((item, index) => {
            const isLoopClone = index >= items.length;
            const waterfallIndex = isLoopClone ? null : index;
            const itemStyle =
              waterfallIndex === null
                ? undefined
                : ({
                    "--cc-hero-services-ticker-enter-delay": `${HERO_TICKER_ENTER_BASE_DELAY_S + waterfallIndex * HERO_TICKER_ENTER_STAGGER_S}s`,
                  } as CSSProperties);

            return (
              <li
                key={`${item.id}-${index}`}
                className={`cc-hero__services-ticker-item${waterfallIndex === null ? " cc-hero__services-ticker-item--instant" : ""}`}
                data-waterfall={waterfallIndex ?? undefined}
                style={itemStyle}
                aria-hidden={isLoopClone ? true : undefined}
              >
                <Link
                  href={item.href}
                  className="cc-hero__services-ticker-link"
                  tabIndex={isLoopClone ? -1 : undefined}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
